<template>
    <div class="d-flex flex-column" style="height: 100%; min-height: 0;">
        <!-- Two siblings: content + footer -->
        
        <!-- Content -->
        <v-container
            class="flex-grow-1 d-flex flex-column"
            :class="{ 'justify-start': isEditing }"
            :style="{
                height: '100%',
                minHeight: 0,
                overflowY: isEditing ? 'auto' : 'hidden',
            }"
        >
        <DataFetchLoadingComponent :isLoading="isLoadingFirestore" />

        <!-- Project Name Section with Edit Icon -->
        <v-row class="flex-grow-0 flex-shrink-0 pb-0">
            <v-col class="pb-0">
                <div v-if="isEditing">
                    <v-text-field
                        v-model="editableProject.name"
                        label="Project Name"
                        variant="outlined"
                        hide-details
                    ></v-text-field>
                </div>
                <div class="d-flex justify-center ga-5 align-center" v-else>
                    <h1>{{ project.name }}</h1>
                    <v-icon
                        v-if="canEditProject"
                        color="primary"
                        @click="toggleEdit"
                    >
                        mdi-pencil
                    </v-icon>
                </div>
            </v-col>
        </v-row>

        <!-- Project Description Section -->
        <v-row class="flex-grow-0 flex-shrink-0">
            <v-col v-if="isEditing">
                <v-textarea
                    v-model="editableProject.description"
                    label="Project Description"
                    variant="outlined"
                ></v-textarea>
            </v-col>
            <v-col
                cols="12"
                class="d-flex justify-center text-wrap pb-0"
                style="white-space: pre-wrap; word-break: break-word"
                v-else
            >
                {{ project.description }}
            </v-col>
        </v-row>

        <!-- Project Client Section -->
        <!-- flex-grow-0: Vuetify v-row defaults to flex-grow, which spreads fields on tall screens -->
        <v-row class="flex-grow-0 flex-shrink-0" v-if="isEditing">
            <v-col>
                <v-combobox
                    v-model="editableProject.client"
                    label="Client"
                    variant="outlined"
                    clearable
                    hide-details
                    :items="clientsOptions"
                ></v-combobox>
            </v-col>
        </v-row>

        <!-- Parent project + Odoo ID + Supli Odoo ID + separate-export switch -->
        <v-row class="flex-grow-0 flex-shrink-0" v-if="isEditing">
            <v-col cols="12" sm="3">
                <v-autocomplete
                    v-model="editableProject.parentProjectId"
                    label="Parent project"
                    variant="outlined"
                    hide-details
                    clearable
                    :items="parentProjectOptions"
                    item-title="title"
                    item-value="value"
                    @update:modelValue="onParentProjectChange"
                />
            </v-col>
            <v-col cols="12" sm="3">
                <v-text-field
                    v-model="editableProject.odooId"
                    label="Odoo ID"
                    variant="outlined"
                    hide-details
                    :disabled="hasParentProject"
                ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
                <v-text-field
                    v-model="editableProject.odooIdSupli"
                    label="Supli Odoo ID"
                    variant="outlined"
                    hide-details
                ></v-text-field>
            </v-col>
            <v-col
                cols="12"
                sm="3"
                class="d-flex align-center justify-center"
            >
                <v-switch
                    v-model="editableProject.separateOdooEntries"
                    label="Separate Odoo Entries for Export"
                    color="primary"
                    hide-details
                    density="compact"
                />
            </v-col>
        </v-row>

        <!-- Project Technical Support Section -->
        <v-row class="flex-grow-0 flex-shrink-0" v-if="isEditing">
            <v-col>
                <v-text-field
                    v-model="editableProject.technicalSupport"
                    label="Technical Support"
                    variant="outlined"
                    hide-details
                ></v-text-field>
            </v-col>
        </v-row>

        <!-- Team Leaders (1–2) -->
        <v-row class="flex-grow-0 flex-shrink-0" v-if="isEditing">
            <v-col>
                <v-autocomplete
                    v-model="editableProject.teamLeaders"
                    label="Team Leaders"
                    variant="outlined"
                    :items="orderedTeamLeaderOptions"
                    item-title="title"
                    item-value="value"
                    multiple
                    chips
                    closable-chips
                    clearable
                    :hide-details="hasTeamLeaders"
                    :error="!hasTeamLeaders"
                    :error-messages="
                        hasTeamLeaders
                            ? []
                            : [
                                  'There needs to be at least one team leader associated to a project.',
                              ]
                    "
                    @update:modelValue="onTeamLeadersChange"
                />
            </v-col>
        </v-row>

        <!-- Project status -->
        <v-row class="flex-grow-0 flex-shrink-0" v-if="isEditing">
            <v-col>
                <v-select
                    v-model="editableProject.isActive"
                    label="Status"
                    variant="outlined"
                    hide-details
                    :items="projectStatusOptions"
                    item-title="title"
                    item-value="value"
                />
            </v-col>
        </v-row>

        <!-- Users Working on Project Table -->
        <v-row class="flex-grow-1" style="min-height: 0;" v-if="!isEditing">
            <v-col class="d-flex flex-column" style="max-height: 100%;">
                <div class="d-flex justify-start mb-3 flex-shrink-0">
                    <h4>PROJECTS {{ periodSuffixLabel }}</h4>
                    <ProjectsPeriodFilterMenu
                        :projectNames="{}"
                        :priorityMap="{}"
                        :disabled="false"
                        @period-changed="handlePeriodChange"
                    />
                </div>

                <div class="flex-grow-1 d-flex flex-column" style="min-height: 0;">
                    <ProjectsTable
                        :tableData="userReportData"
                        :headers="userTableHeaders"
                        :enableReordering="false"
                        :enablePriority="false"
                        :enableClickNavigation="true"
                        :navigationRoute="'UserInfo'"
                        :navigationParam="'uid'"
                        :currentTab="'users'"
                        :showActiveEntryTime="true"
                        :totalTimeWorked="selectedPeriod !== 'today' ? totalTimeWorked : null"
                        :totalOvertimeWorked="selectedPeriod !== 'today' ? totalOvertimeWorked : null"
                    />
                </div>
            </v-col>
        </v-row>

        <!-- Project Priority Section -->
        <v-row class="flex-grow-0 flex-shrink-0">
            <v-col>
                <v-select
                    v-model="editableProject.priority"
                    :items="priorities"
                    label="Priority"
                    @update:modelValue="
                        (newValue) => updatePriority(newValue)
                    "
                    hide-details
                    variant="outlined"
                />
            </v-col>
        </v-row>
    </v-container>
    
    <!-- Footer - only when editing -->
    <BottomButtonWrapper :isShowing="isEditing">
        <v-col cols="6">
            <ThemedActionButton
                :buttonColor="'error'"
                :text="'CANCEL'"
                @click="cancelEdit"
            />
        </v-col>
        <v-col cols="6">
            <ThemedActionButton
                :buttonColor="'success'"
                :text="'SAVE'"
                :disabled="!hasTeamLeaders"
                @click="saveProjectChanges"
            />
        </v-col>
    </BottomButtonWrapper>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
    calculateUsersWorkSummaryForProject,
    getMergedTimeEntriesForUsers,
    calculateTotalTimeWorked,
} from "@/utils/utils";
import {
    getProjectTeamLeaders,
    getParentProjectOptions,
    isProjectActive,
    isUserActive,
    normalizeTeamLeadersForSave,
} from "@/utils/projectUtils";
import BottomButtonWrapper from "./BottomButtonWrapper.vue";
import ThemedActionButton from "./ThemedActionButton.vue";
import ProjectsTable from "./ProjectsTable.vue";
import ProjectsPeriodFilterMenu from "./ProjectsPeriodFilterMenu.vue";
import DataFetchLoadingComponent from "@/components/DataFetchLoadingComponent.vue";
import {
    periodSuffix,
    getDateRangeForPeriod,
    shouldFetchFromFirestore,
} from "@/utils/firestoreUtils";
import dayjs from "@/plugins/dayjs";

export default {
    name: "ProjectInfo",

    /**
     * Components registered in this component.
     */
    components: {
        BottomButtonWrapper,
        ThemedActionButton,
        ProjectsTable,
        ProjectsPeriodFilterMenu,
        DataFetchLoadingComponent,
    },

    /**
     * Reactive data properties for the component.
     * @returns {Object} The reactive data for this component.
     */
    data() {
        return {
            /**
             * Boolean indicating whether the project is in edit mode.
             * @type {Boolean}
             */
            isEditing: false,

            /**
             * Object representing editable fields for the project.
             * @type {Object}
             */
            editableProject: {
                name: "",
                description: "",
                client: "",
                parentProjectId: null,
                odooId: "",
                odooIdSupli: "",
                separateOdooEntries: false,
                technicalSupport: "",
                teamLeaders: [],
                isActive: true,
            },

            projectStatusOptions: [
                { title: "Active", value: true },
                { title: "Inactive", value: false },
            ],
            priorities: ["High", "Normal", "Low"],
            selectedPeriod: "today",
            userReportData: [],
            /**
             * Loading state for Firestore data fetching.
             * @type {Boolean}
             * @default false
             */
            isLoadingFirestore: false,
        };
    },

    /**
     * Computed properties derived from Vuex state or internal data.
     */
    computed: {
        ...mapGetters([
            "getUsers", // Fetches all users from Vuex store.
            "getManagedProjects", // Fetches all managed projects from Vuex store.
            "getAllProjects",
            "getCompanies", // Fetches all companies from Vuex store.
            "getRights", // Fetches user rights.
            "getUser", // Fetches current user.
        ]),
        periodSuffixLabel() {
            return periodSuffix(this.selectedPeriod);
        },
        users() {
            return this.getUsers || [];
        },
        managedProjects() {
            return this.getManagedProjects || [];
        },
        projectId() {
            return this.$route.params.projectId;
        },
        project() {
            return (
                this.managedProjects.find(
                    (project) => project.projectId === this.projectId
                ) || {}
            );
        },

        /**
         * Retrieves client options based on the companies fetched from Vuex.
         * @returns {Array<string>} Array of client titles (company names).
         */
        clientsOptions() {
            const options = (this.getCompanies || []).map(
                (company) => company.title
            ); // Extract titles as strings
            return options;
        },

        hasTeamLeaders() {
            return (this.editableProject.teamLeaders || []).length > 0;
        },
        hasParentProject() {
            return Boolean(this.editableProject.parentProjectId);
        },
        parentProjectOptions() {
            const source =
                Array.isArray(this.getAllProjects) && this.getAllProjects.length
                    ? this.getAllProjects
                    : this.managedProjects || [];
            return getParentProjectOptions(source, this.projectId);
        },
        /**
         * Active users as team-lead options; selected UIDs listed first, then A–Z.
         */
        orderedTeamLeaderOptions() {
            const selected = this.editableProject.teamLeaders || [];
            const options = (this.getUsers || [])
                .filter((user) => isUserActive(user))
                .map((user) => {
                    const displayName =
                        user.name && user.surname
                            ? `${user.name} ${user.surname}`
                            : user.email || "Unknown User";
                    return {
                        title: displayName,
                        value: user.uid,
                    };
                })
                .sort((a, b) => a.title.localeCompare(b.title));

            const selectedOpts = [];
            selected.forEach((uid) => {
                const opt = options.find((o) => o.value === uid);
                if (opt) selectedOpts.push(opt);
            });
            const rest = options.filter((o) => !selected.includes(o.value));
            return [...selectedOpts, ...rest];
        },
        /**
         * Checks if the current user can edit this project.
         * - Only admins can edit projects (viewProjects includes "all")
         * - Team leads have read-only access
         * @returns {Boolean} True if the user can edit the project, false otherwise.
         */
        canEditProject() {
            const rights = this.getRights;
            if (!rights) return false;

            // Only admins can edit projects
            return rights.viewProjects?.includes("all") || false;
        },

        /**
         * Table headers for the user report table.
         * @returns {Array} Array of header objects.
         */
        userTableHeaders() {
            return [
                {
                    title: "User",
                    value: "userName",
                    width: "40",
                    sortable: true,
                },
                {
                    title: "Hours",
                    value: "totalTimeWorked",
                    width: "30",
                },
                {
                    title: "Overtime",
                    value: "overtimeTimeWorked",
                    width: "30",
                },
            ];
        },

        /**
         * Calculates total time worked across all users using the existing utility function.
         * Reuses calculateTotalTimeWorked from utils.js following DRY principle.
         * @returns {string|null} Formatted total time string (e.g., "8h:45min") or null if no data
         */
        totalTimeWorked() {
            if (!this.userReportData || this.userReportData.length === 0) {
                return null;
            }

            // Map userReportData to the format expected by calculateTotalTimeWorked
            // (array of objects with totalTimeWorked property)
            const totalTimeRows = this.userReportData.map((user) => ({
                projectName: user.userName, // Not used by calculateTotalTimeWorked, but required by structure
                totalTimeWorked: user.totalTimeWorked || "0h:0min",
            }));

            return calculateTotalTimeWorked(totalTimeRows);
        },

        /**
         * Calculates total overtime worked across all users using the existing utility function.
         * Reuses calculateTotalTimeWorked by mapping overtimeTimeWorked to totalTimeWorked,
         * @returns {string|null} Formatted total overtime string (e.g., "2h:15min") or null if no data
         */
        totalOvertimeWorked() {
            if (!this.userReportData || this.userReportData.length === 0) {
                return null;
            }

            const overtimeRows = this.userReportData.map((user) => ({
                projectName: user.userName,
                totalTimeWorked: user.overtimeTimeWorked || "0h:0min",
            }));

            return calculateTotalTimeWorked(overtimeRows);
        },
    },

    /**
     * Watches for changes to reactive properties and triggers associated logic.
     */
    watch: {
        /**
         * Sync editable form from the live project, but not while editing —
         * RTDB project snapshots (e.g. activeUsers) would otherwise overwrite
         * in-progress changes like removed team-leader chips.
         */
        project: {
            immediate: true,
            handler(newProject) {
                if (this.isEditing) return;
                this.editableProject = this.snapshotEditableProject(newProject);
            },
        },
    },

    /**
     * Methods for managing project data and interactions.
     */
    methods: {
        ...mapActions([
            "startProjectsListener", // Starts listening for project updates in Vuex.
            "startUsersListener", // Starts listening for user updates in Vuex.
            "updateProjectInStore", // Updates a project in the Vuex store and Firebase.
        ]),

        snapshotEditableProject(source = {}) {
            const parentProjectId = source.parentProjectId || null;
            const parent = parentProjectId
                ? (this.getAllProjects || this.managedProjects || []).find(
                      (p) => p.projectId === parentProjectId
                  )
                : null;
            return {
                ...source,
                parentProjectId,
                odooId: parent ? parent.odooId || "" : source.odooId || "",
                teamLeaders: getProjectTeamLeaders(source),
                isActive: isProjectActive(source || {}),
                separateOdooEntries: source?.separateOdooEntries === true,
            };
        },

        /**
         * Enables edit mode for the project.
         */
        async toggleEdit() {
            this.editableProject = this.snapshotEditableProject(this.project);
            this.isEditing = true;
        },

        onParentProjectChange(parentId) {
            this.editableProject.parentProjectId = parentId || null;
            if (!parentId) return;
            const parent = (
                this.getAllProjects ||
                this.managedProjects ||
                []
            ).find((p) => p.projectId === parentId);
            this.editableProject.odooId = parent?.odooId || "";
        },

        /**
         * Saves changes made to the project.
         * Updates both the Vuex store and Firebase.
         */
        onTeamLeadersChange(value) {
            const next = Array.isArray(value) ? value.slice(0, 2) : [];
            this.editableProject.teamLeaders = next;
        },
        async saveProjectChanges() {
            if (!this.hasTeamLeaders) return;
            try {
                const leads = normalizeTeamLeadersForSave(
                    this.editableProject.teamLeaders
                );
                const parentProjectId =
                    this.editableProject.parentProjectId || null;
                let odooId = this.editableProject.odooId || "";
                if (parentProjectId) {
                    const parent = (
                        this.getAllProjects ||
                        this.managedProjects ||
                        []
                    ).find((p) => p.projectId === parentProjectId);
                    odooId = parent?.odooId || "";
                }
                const updatedProject = {
                    ...this.editableProject,
                    ...leads,
                    parentProjectId,
                    odooId,
                    isActive: isProjectActive(this.editableProject),
                    separateOdooEntries:
                        this.editableProject.separateOdooEntries === true,
                };
                await this.updateProjectInStore({
                    projectId: this.projectId,
                    updatedProject,
                    previousProject: this.project,
                });
                this.isEditing = false;
            } catch (error) {
                console.error("Error saving project changes:", error);
            }
        },

        /**
         * Cancels editing and resets the editable fields to the original project values.
         */
        cancelEdit() {
            this.editableProject = this.snapshotEditableProject(this.project);
            this.isEditing = false;
        },
        /**
         * Updates the project's priority in Firebase in real time.
         * @param {string} newPriority - The new priority value.
         */
        async updatePriority(newPriority) {
            try {
                this.editableProject.priority = newPriority;
                await this.updateProjectInStore({
                    projectId: this.projectId,
                    updatedProject: { priority: newPriority },
                    previousProject: this.project,
                });
            } catch (error) {
                console.error(
                    "Error updating priority in Firebase and Vuex:",
                    error
                );
            }
        },

        /**
         * Handles period change event from ProjectsPeriodFilterMenu.
         * @param {string} newPeriod - The newly selected period key.
         * @param {Array<Date>|null} customDateRange - Custom date range array (for "custom" period)
         */
        async handlePeriodChange(newPeriod, customDateRange = null) {
            this.selectedPeriod = newPeriod;
            await this.updateUserReportData(newPeriod, customDateRange);
        },
        /**
         * Updates user report data for the selected period, fetching from Firestore if needed.
         * @param {string} periodKey - The period key
         * @param {Array<Date>|null} customDateRange - Custom date range array (for "custom" period)
         */
        async updateUserReportData(periodKey, customDateRange = null) {
            const dateRange = getDateRangeForPeriod(periodKey, customDateRange);
            const { startDate: start, endDate: end } = dateRange;

            // Check if Firestore fetch is needed
            const needsFirestore = shouldFetchFromFirestore(
                periodKey,
                customDateRange
            );

            try {
                if (needsFirestore) {
                    this.isLoadingFirestore = true;
                }

                // Get merged time entries (RTDB + Firestore if needed)
                const mergedTimeEntriesByUser =
                    await getMergedTimeEntriesForUsers(
                        this.users || [],
                        periodKey,
                        customDateRange
                    );

                // Single efficient call: gets all users who worked on project,
                // calculates time for the period, and includes active entries for "today"
                this.userReportData = calculateUsersWorkSummaryForProject(
                    this.users,
                    this.projectId,
                    {
                        dateRange: { startDate: start, endDate: end },
                        excludeEmpty: false,
                        mergedTimeEntriesByUser,
                        includeActiveEntries: periodKey === 'today', // Only show active entries for today
                    }
                );
            } finally {
                this.isLoadingFirestore = false;
            }
        },
    },

    /**
     * Lifecycle hook that initializes the component and starts listeners.
     */
    async created() {
        // Fetch appropriate data based on the role
        this.startProjectsListener();
        this.startUsersListener();
        // Initialize user report data with default period
        await this.updateUserReportData(this.selectedPeriod);
    },
};
</script>

<style scoped>
:deep(.v-input:not(.v-input--error) .v-input__details) {
    display: none;
}
</style>
